import { Button, DropdownMenu, Text } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useQueryParam } from "use-query-params";

import { HardwareRequestStatus } from "src/api/types.generated";
import { Header, PageWrapper } from "src/shared/components";
import { Search } from "src/shared/components/Hardware";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { useHardwareOrganizerAccess } from "src/shared/hooks";
import { ChevronDownIcon } from "src/static/icons";
import globalConstants from "src/theme/globalConstants";

import { useGetAllHardwareCategoriesQuery } from "../graphql/getAllHardwareCategories.generated";
import { useGetAllHardwareSkusQuery } from "../graphql/getAllHardwareSkus.generated";

import HackerRequestHistory from "./hacker";
import OrganizerRequestHistory from "./organizer";

const RequestHistory = () => {
  const isOrganizer = useHardwareOrganizerAccess();
  const [query, setQuery] = useQueryParam<string | undefined>("search");
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined
  );
  const [selectedStatuses, setSelectedStatuses] = useState<
    Set<HardwareRequestStatus>
  >(new Set());
  const [checkedHideLottery, setCheckedHideLottery] = useState(false);

  // Load persisted status filters from localStorage
  useEffect(() => {
    const savedStatuses = localStorage.getItem("hardware-status-filters");
    if (savedStatuses) {
      try {
        const parsedStatuses = JSON.parse(savedStatuses);
        setSelectedStatuses(new Set(parsedStatuses));
      } catch (error) {
        console.error("Failed to parse saved status filters:", error);
      }
    }
  }, []);

  // Save status filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      "hardware-status-filters",
      JSON.stringify(Array.from(selectedStatuses))
    );
  }, [selectedStatuses]);

  const toggleStatus = useCallback((status: HardwareRequestStatus) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  }, []);

  const clearAllStatuses = useCallback(() => {
    setSelectedStatuses(new Set());
  }, []);

  const selectAllStatuses = useCallback(() => {
    const allStatuses = Object.values(HardwareRequestStatus).filter(
      (status) => isOrganizer || status !== HardwareRequestStatus.Waiting
    );
    setSelectedStatuses(new Set(allStatuses));
  }, [isOrganizer]);

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === "") {
        setQuery(undefined);
      } else {
        setQuery(e.currentTarget.value);
      }
    },
    [setQuery]
  );

  const { data: hardwareItemsData } = useGetAllHardwareSkusQuery();

  const filteredHardwareItems = useMemo(() => {
    if (!hardwareItemsData?.getHardwareSkus) return [];

    if (selectedCategory === undefined || selectedCategory === -1) {
      return hardwareItemsData.getHardwareSkus;
    }

    return hardwareItemsData.getHardwareSkus.filter(
      (item) => item.category_id === selectedCategory
    );
  }, [hardwareItemsData, selectedCategory]);

  const { data: hardwareCategoriesData } = useGetAllHardwareCategoriesQuery({
    variables: { event_slug: CURRENT_EVENT_SLUG },
    fetchPolicy: "cache-first",
  });

  const hardwareCategories = useMemo(() => {
    return hardwareCategoriesData?.getHardwareCategories || [];
  }, [hardwareCategoriesData]);

  return (
    <>
      <Header
        title="Hardware"
        subtitle="Browse and request a variety of hardware to bring your project to life!"
      />
      <PageWrapper>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginBottom: "48px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Text type="h2">
              {isOrganizer ? "Request History" : "Your Requests"}
            </Text>
            <Text
              type="body1"
              style={{ color: globalConstants?.color?.textSecondary as string }}
            >
              {isOrganizer
                ? "View all hardware requests."
                : "View all of your hardware requests."}
            </Text>
          </div>

          {isOrganizer && (
            <FlexRow>
              <SearchWrapper>
                <Search
                  query={query || ""}
                  onSearchChange={onSearchChange}
                  hideResults
                  placeholder="Search by item, requester, or request #"
                />
              </SearchWrapper>
              <DropdownMenu
                items={[
                  [
                    {
                      content: <Text type="body1">All</Text>,
                      action: () => setSelectedCategory(-1),
                      align: "left" as const, // need const for typecheck
                    },
                  ],
                  hardwareCategories?.map((category) => ({
                    content: <Text type="body1">{category.name}</Text>,
                    action: () => setSelectedCategory(category.id),
                    align: "left",
                  })) || [],
                ]}
                style={{ justifyContent: "start" }}
              >
                <Button>
                  {hardwareCategories.find(
                    (category) => category.id === selectedCategory
                  )?.name || "All"}
                  <ChevronDownIcon />
                </Button>
              </DropdownMenu>
            </FlexRow>
          )}
        </div>

        {isOrganizer ? (
          <OrganizerRequestHistory
            items={filteredHardwareItems}
            query={query}
            selectedStatuses={selectedStatuses}
            hideLottery={checkedHideLottery}
            onToggleStatus={toggleStatus}
            onClearAllStatuses={clearAllStatuses}
            onSelectAllStatuses={selectAllStatuses}
            onHideLotteryChange={setCheckedHideLottery}
          />
        ) : (
          <HackerRequestHistory
            items={filteredHardwareItems}
            query={query}
            selectedStatuses={selectedStatuses}
            onToggleStatus={toggleStatus}
            onClearAllStatuses={clearAllStatuses}
            onSelectAllStatuses={selectAllStatuses}
          />
        )}
      </PageWrapper>
    </>
  );
};

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SearchWrapper = styled.div`
  width: 100%;
`;

export default RequestHistory;
